import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Group, Paper, Text, Tooltip } from '@mantine/core';
import { IconFocusCentered, IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import type { AccountDto, AccountNode } from './types';
import { ACCOUNT_TYPE } from './types';

// Canvas view of the chart of accounts: a horizontal tidy tree rendered
// as SVG with wheel-zoom (around the cursor) and drag-panning. Hand-
// rolled instead of pulling a graph library — ~35 nodes don't justify
// a dependency, and we keep full control of RTL mirroring: in Arabic
// the tree grows right-to-left from the roots.

const COL_W = 240;   // horizontal distance between depths
const ROW_H = 52;    // vertical distance between leaves
const NODE_W = 200;
const NODE_H = 40;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2.5;

interface LaidOutNode {
  node: AccountNode;
  x: number; // start-edge of the node box (already direction-mirrored)
  y: number; // vertical center
  parentId: string | null;
}

interface Props {
  tree: AccountNode[];
  onNodeClick: (account: AccountDto) => void;
}

export function AccountTreeCanvas({ tree, onNodeClick }: Props) {
  const { t, i18n } = useTranslation();
  const rtl = i18n.dir() === 'rtl';
  const svgRef = useRef<SVGSVGElement | null>(null);

  const { nodes, byId, height } = useMemo(() => layoutTree(tree, rtl), [tree, rtl]);

  const [zoom, setZoom] = useState(0.9);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 60, y: 40 });
  const drag = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // RTL coordinates are negative (the tree grows leftward from 0), so
  // the home position anchors the origin near the container's right
  // edge. Measured, not guessed — hence the layout effect.
  const homePan = useCallback(() => {
    const width = svgRef.current?.getBoundingClientRect().width ?? 900;
    return { x: rtl ? width - 60 : 60, y: 40 };
  }, [rtl]);

  useLayoutEffect(() => {
    setPan(homePan());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtl]);

  const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  const zoomAt = useCallback((factor: number, cx: number, cy: number) => {
    setZoom((z) => {
      const next = clampZoom(z * factor);
      const ratio = next / z;
      setPan((p) => ({ x: cx - (cx - p.x) * ratio, y: cy - (cy - p.y) * ratio }));
      return next;
    });
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent<SVGSVGElement>) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      zoomAt(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX - rect.left, e.clientY - rect.top);
    },
    [zoomAt],
  );

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!drag.current) return;
    setPan({
      x: drag.current.panX + (e.clientX - drag.current.startX),
      y: drag.current.panY + (e.clientY - drag.current.startY),
    });
  };
  const endDrag = () => {
    drag.current = null;
    setDragging(false);
  };

  const resetView = () => {
    setZoom(0.9);
    setPan(homePan());
  };

  const zoomButtons = (
    <Group gap={4}>
      <Tooltip label={t('accounting.chart.canvas.zoomIn')}>
        <ActionIcon variant="default" onClick={() => centerZoom(1.25)}>
          <IconZoomIn size={16} stroke={1.6} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t('accounting.chart.canvas.zoomOut')}>
        <ActionIcon variant="default" onClick={() => centerZoom(1 / 1.25)}>
          <IconZoomOut size={16} stroke={1.6} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t('accounting.chart.canvas.reset')}>
        <ActionIcon variant="default" onClick={resetView}>
          <IconFocusCentered size={16} stroke={1.6} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  function centerZoom(factor: number) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    zoomAt(factor, rect.width / 2, rect.height / 2);
  }

  return (
    <Paper p={0} className="relative overflow-hidden" style={{ height: 'calc(100vh - 320px)', minHeight: 420 }}>
      <div className="absolute top-3 z-10" style={{ insetInlineEnd: 12 }}>
        {zoomButtons}
      </div>
      <div className="absolute bottom-3 z-10 pointer-events-none" style={{ insetInlineStart: 12 }}>
        <Text size="xs" c="dimmed">{t('accounting.chart.canvas.hint')}</Text>
      </div>
      <svg
        ref={svgRef}
        className="h-full w-full touch-none select-none"
        style={{ cursor: dragging ? 'grabbing' : 'grab', direction: 'ltr' }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
          {/* Edges first, under the nodes. */}
          {nodes.map((n) => {
            if (n.parentId === null) return null;
            const parent = byId.get(n.parentId)!;
            return (
              <path
                key={`e-${n.node.accountId}`}
                d={edgePath(parent, n, rtl)}
                fill="none"
                stroke="var(--wheels-border)"
                strokeWidth={1.5}
              />
            );
          })}
          {nodes.map((n) => (
            <CanvasNode key={n.node.accountId} laid={n} rtl={rtl} onClick={onNodeClick} />
          ))}
          {/* Invisible sizer keeps the group's bbox stable. */}
          <rect x={-COL_W} y={-ROW_H} width={COL_W} height={height + 2 * ROW_H} fill="none" />
        </g>
      </svg>
    </Paper>
  );
}

function CanvasNode({
  laid, rtl, onClick,
}: { laid: LaidOutNode; rtl: boolean; onClick: (a: AccountDto) => void }) {
  const { node, x, y } = laid;
  const color = colorForType(node.accountType);
  const name = rtl ? node.nameAr : (node.nameEn ?? node.nameAr);
  return (
    <g
      transform={`translate(${x} ${y - NODE_H / 2})`}
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(node);
      }}
    >
      <rect
        width={NODE_W}
        height={NODE_H}
        rx={8}
        fill="white"
        stroke={color}
        strokeWidth={node.isPostable ? 1.2 : 2}
        opacity={node.isActive ? 1 : 0.5}
      />
      <rect width={4} height={NODE_H} rx={2} x={rtl ? NODE_W - 4 : 0} fill={color} />
      <text
        x={rtl ? NODE_W - 14 : 14}
        y={17}
        textAnchor={rtl ? 'end' : 'start'}
        fontSize={11}
        fontFamily="inherit"
        fill="var(--wheels-ink-muted)"
      >
        {node.code}
      </text>
      <text
        x={rtl ? NODE_W - 14 : 14}
        // direction=rtl flips what 'start' means: it anchors the RIGHT
        // end of the Arabic text — exactly the inner edge we want.
        textAnchor="start"
        y={31}
        fontSize={12.5}
        fontWeight={node.isPostable ? 500 : 700}
        fontFamily="inherit"
        fill="var(--wheels-ink-strong)"
        direction={rtl ? 'rtl' : 'ltr'}
      >
        {truncate(name, 22)}
      </text>
    </g>
  );
}

/** Horizontal tidy tree: leaves get sequential rows, parents center on
 * their children. In RTL the x axis is mirrored so roots sit at the
 * right and the tree grows leftward. */
function layoutTree(tree: AccountNode[], rtl: boolean) {
  const nodes: LaidOutNode[] = [];
  let nextRow = 0;

  const place = (node: AccountNode, depth: number, parentId: string | null): number => {
    let y: number;
    if (node.children.length === 0) {
      y = nextRow * ROW_H;
      nextRow += 1;
    } else {
      const childYs = node.children.map((c) => place(c, depth + 1, node.accountId));
      y = (Math.min(...childYs) + Math.max(...childYs)) / 2;
    }
    const xLtr = depth * COL_W;
    const x = rtl ? -(xLtr + NODE_W) : xLtr;
    nodes.push({ node, x, y, parentId });
    return y;
  };

  for (const root of tree) {
    place(root, 0, null);
    nextRow += 0.5; // breathing room between root groups
  }

  const byId = new Map(nodes.map((n) => [n.node.accountId, n]));
  return { nodes, byId, height: nextRow * ROW_H };
}

/** Cubic curve from the parent's outer edge to the child's inner edge. */
function edgePath(parent: LaidOutNode, child: LaidOutNode, rtl: boolean): string {
  const x1 = rtl ? parent.x : parent.x + NODE_W;
  const x2 = rtl ? child.x + NODE_W : child.x;
  const midX = (x1 + x2) / 2;
  return `M ${x1} ${parent.y} C ${midX} ${parent.y}, ${midX} ${child.y}, ${x2} ${child.y}`;
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function colorForType(type: number): string {
  switch (type) {
    case ACCOUNT_TYPE.ASSET: return 'var(--mantine-color-blue-6)';
    case ACCOUNT_TYPE.LIABILITY: return 'var(--mantine-color-orange-6)';
    case ACCOUNT_TYPE.EQUITY: return 'var(--mantine-color-violet-6)';
    case ACCOUNT_TYPE.REVENUE: return 'var(--mantine-color-green-6)';
    case ACCOUNT_TYPE.EXPENSE: return 'var(--mantine-color-red-6)';
    default: return 'var(--wheels-border)';
  }
}
